/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import React, { useRef, Suspense, lazy, useEffect, useCallback, useState } from 'react';

import { useAppDispatch, useAppSelector } from '../../../../../app/hooks';
import { selectPatientContext } from '../../../Fhir/fhirSlice';
import { clearSummary, resetThreePaneViewStates, selectPatientSummaryState } from './patientSummarySlice';
import styles from './patientSummary.module.scss';
import { copyToClipboard } from '../../../utils';
import { analyticsTrack } from '../../../analytics_utils';
import { selectPearlUserInfo } from '../../../Login/userSlice';
import { selectDocumentVersionById, selectMessages } from '../../chatSlice';
import { useEnableEpicEmbeddedView } from '../../../permissions';
import { IHumanName } from '@ahryman40k/ts-fhir-types/lib/R4';

const CKEditor = lazy(() => import('./MyCKEditor'));

export const PatientSummary = () => {
  const messages = useAppSelector(selectMessages);
  const documentVersionId = messages?.find((m) => m.pages?.[0])?.pages![0]!.document_version_id;
  const documentVersion = useAppSelector((state) => selectDocumentVersionById(state, documentVersionId));
  const pearlUserInfo = useAppSelector(selectPearlUserInfo);
  const summaryRef = useRef<HTMLDivElement | null>(null);
  const patientContext = useAppSelector(selectPatientContext);
  const enableEpicEmbeddedView = useEnableEpicEmbeddedView();

  const dispatch = useAppDispatch();
  const { showSummary, summary, fetchingSummary, showContextualization, showTopic } =
    useAppSelector(selectPatientSummaryState);

  const [editorData, setEditorData] = useState('');

  const copySummary = () => {
    const div = document.createElement('div');
    div.innerHTML = editorData;
    document.body.appendChild(div);
    copyToClipboard(div);
    div.remove();
  };

  useEffect(() => {
    setEditorData(summary);
  }, [summary]);

  const resetSummary = useCallback(() => {
    dispatch(clearSummary());
    dispatch(resetThreePaneViewStates());
  }, [dispatch]);

  const copyButtonHandler = () => {
    copySummary();
    analyticsTrack(
      'summary_action',
      {
        action: 'copy_summary',
        documentName: documentVersion?.title,
        documentId: documentVersion?.document.id,
        documentVersionId: documentVersion?.id,
        documentSlug: documentVersion?.document?.slug,
        documentType: documentVersion?.document?.docType,
      },
      pearlUserInfo
    );
  };

  useEffect(() => {
    !enableEpicEmbeddedView && resetSummary();
    // resetSummary on component unmounting
    return () => {
      !enableEpicEmbeddedView && resetSummary();
    };
  }, [patientContext?.patient, resetSummary, enableEpicEmbeddedView]);

  // As the three pane view is causing summary to go beyond view port in smaller screens
  // we are bringing summary into view port when it is shown
  useEffect(() => {
    if (showSummary && summary?.toString().trim().length > 0 && !!summaryRef.current) {
      summaryRef.current.scrollIntoView();
    }
  }, [showSummary, summary]);

  return showSummary ? (
    <Paper
      className={styles.summaryContainer}
      data-private
      ref={summaryRef}
      sx={{
        flexGrow: !showTopic || !showContextualization ? 1 : 0,
        flexBasis: showSummary && (!showTopic || !showContextualization) ? '50%' : undefined,
      }}
    >
      <div className={styles.summaryAction}>
        <div className={styles.summaryActionContainer}>
          <Typography className={styles.summaryTitle}>
            {patientNameToString(patientContext?.patient?.name)} Summary
          </Typography>
          <Button
            className={styles.copyButton}
            variant="copy"
            onClick={copyButtonHandler}
            disableRipple
            disabled={fetchingSummary}
          >
            Copy
          </Button>
        </div>
      </div>
      <div id={enableEpicEmbeddedView ? 'epic-summary-editor' : 'summary-editor'} className={styles.summaryEditor}>
        <Suspense fallback={<span>Loading editor...</span>}>
          <CKEditor data={editorData} onChange={setEditorData} />
        </Suspense>
      </div>
    </Paper>
  ) : null;
};

export function patientNameToString(names: IHumanName[] | undefined) {
  const name = names?.[0];
  if (name?.text) return name?.text;
  if (name?.family || name?.given?.[0]) return `${name?.given?.[0]} ${name?.family}`;
  return '';
}
